/**
 * Slug utilities.
 *
 * Slugs must be:
 *   - all lowercase
 *   - ASCII-only (no Devanagari, em dashes, smart quotes, emoji)
 *   - words separated by single hyphens (Google's preferred URL convention)
 *   - free of leading or trailing hyphens
 *
 * These rules match what tends to rank in Nepali-English SERPs and what
 * Vercel, Cloudflare, and CDN cache keys treat as canonical.
 */

const DIACRITIC_MAP: Record<string, string> = {
  // Common smart punctuation that sneaks in from Word / Google Docs paste.
  "—": "-", // em dash
  "–": "-", // en dash
  "‐": "-", // hyphen
  "‑": "-", // non-breaking hyphen
  "‘": "",  // left single quote
  "’": "",  // right single quote / apostrophe
  "“": "",  // left double quote
  "”": "",  // right double quote
  " ": " ", // non-breaking space
};

/** Strip combining marks (é → e, ñ → n) so the URL stays ASCII. */
function stripDiacritics(input: string): string {
  return input.normalize("NFKD").replace(/[̀-ͯ]/g, "");
}

/** Replace smart punctuation with their plain equivalents. */
function normalizePunctuation(input: string): string {
  let out = input;
  for (const [from, to] of Object.entries(DIACRITIC_MAP)) {
    out = out.split(from).join(to);
  }
  return out;
}

/**
 * Convert a string to a SEO-friendly slug.
 *   "IOE Pre-Engineering Class 2083" → "ioe-pre-engineering-class-2083"
 *   "What's up — Nepal!?"           → "whats-up-nepal"
 */
export function generateSlug(text: string): string {
  if (!text) return "";
  return normalizePunctuation(stripDiacritics(text))
    .toLowerCase()
    .trim()
    .replace(/[^\w\s-]/g, "") // drop anything that isn't [a-z0-9_-] or whitespace
    .replace(/_/g, "-")        // underscores are not SEO-friendly word separators
    .replace(/\s+/g, "-")      // whitespace runs become a single hyphen
    .replace(/-+/g, "-")       // collapse repeated hyphens
    .replace(/^-+|-+$/g, "");  // trim leading and trailing hyphens
}

/**
 * Create a slug-id pair from a classroom name and its Mongo ObjectId.
 *   "IOE Pre-Engineering", "507f1f77bcf86cd799439011"
 *     → "ioe-pre-engineering-507f1f77bcf86cd799439011"
 */
export function createClassroomSlug(
  classroomName: string,
  classroomId: string,
): string {
  const slugPart = generateSlug(classroomName);
  if (!classroomId) return slugPart;
  return slugPart ? `${slugPart}-${classroomId}` : classroomId;
}

/**
 * Extract the id from a slug-id URL parameter.
 * Assumes the id is the last hyphen-separated segment (Mongo ObjectId pattern).
 *   "ioe-pre-engineering-507f1f77bcf86cd799439011" → "507f1f77bcf86cd799439011"
 */
export function extractIdFromSlug(slugParam: string): string {
  if (!slugParam) return "";
  const parts = slugParam.split("-");
  return parts[parts.length - 1] || "";
}

/**
 * Build the canonical URL for a classroom page, given its name and id.
 * Convenience helper so call sites don't repeat the template string.
 */
export function classroomUrl(
  name: string,
  id: string,
  baseUrl = "https://clamphook.com",
): string {
  return `${baseUrl}/classrooms/${createClassroomSlug(name, id)}`;
}
